/**
 * Live Brand Bible location-merge detection.
 * Reads wizard-store locations, maps them to typed address parts (or parses
 * the legacy `address` string with the same rules as
 * backend/scripts/bb_location_backfill.py parse_address), then flags
 * parts-equal twins via findDuplicatePairs. Never merges.
 */
import { useMemo } from 'react';

import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry } from '@/features/brand-bible-wizard/types';
import {
  findDuplicatePairs,
  partsEqual,
  pickKeepRow,
  type AddressParts,
  type MergeRow,
} from '../sections/locationMerge';
import type { LocationMergeCandidate } from '../sections/LocationMergePanel';

/** Narrow extras another lane may already have written onto the location row. */
type LocationPartFields = {
  street?: string | null;
  suite?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

type LocationRowExtras = LocationPartFields & {
  enrichment?: LocationMergeCandidate['enrichment'];
};

const STATE_ZIP = /^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;
const SUITE = /^\s*(ste\.?|suite|unit|apt\.?|#)\b/i;

/**
 * Port of backend/scripts/bb_location_backfill.py parse_address.
 * Comma-split, drop empty tokens (comma-double twins parse equal), last token
 * is `ST ZIP`, suite-marker tokens peel off, remainder is street.
 */
export function parseAddress(raw: string | null | undefined): AddressParts {
  const parts: AddressParts = {};
  if (typeof raw !== 'string' || !raw.trim()) return parts;
  let tokens = raw.split(',').map((t) => t.trim()).filter(Boolean);
  if (tokens.length === 0) return parts;

  const tail = tokens[tokens.length - 1].match(STATE_ZIP);
  if (tail) {
    parts.state = tail[1].toUpperCase();
    parts.zip = tail[2];
    tokens = tokens.slice(0, -1);
  }
  if (tokens.length > 0) {
    parts.city = tokens[tokens.length - 1];
    tokens = tokens.slice(0, -1);
  }
  const suiteTokens = tokens.filter((t) => SUITE.test(t));
  const streetTokens = tokens.filter((t) => !SUITE.test(t));
  if (suiteTokens.length > 0) parts.suite = suiteTokens.join(', ');
  if (streetTokens.length > 0) parts.street = streetTokens.join(', ');
  return parts;
}

function typedParts(row: BbEntry<'locations'>): AddressParts | null {
  const extra = row as BbEntry<'locations'> & LocationPartFields;
  const street = extra.street;
  const suite = extra.suite;
  const city = extra.city;
  const state = extra.state;
  const zip = extra.zip;
  if (street == null && suite == null && city == null && state == null && zip == null) {
    return null;
  }
  return { street, suite, city, state, zip };
}

function rowParts(row: BbEntry<'locations'>): AddressParts {
  return typedParts(row) ?? parseAddress(row.address);
}

function isPrimaryKey(locationKey: string | undefined): boolean {
  return locationKey === 'primary';
}

function toMergeRow(row: BbEntry<'locations'>): MergeRow {
  return {
    id: row.id,
    locationKey: row.location_key ?? '',
    verified: Boolean(row.verified_by),
    createdAt: row.created_at,
    ...rowParts(row),
  };
}

export function displayLocationKey(locationKey: string): string {
  if (!locationKey || isPrimaryKey(locationKey)) return 'Primary';
  return locationKey
    .replace(/[-_]+/g, ' ')
    .replace(/\b([a-z])/g, (ch) => ch.toUpperCase());
}

export function getLocationParts(row: BbEntry<'locations'>): AddressParts {
  return rowParts(row);
}

export function hasTypedLocationParts(row: BbEntry<'locations'>): boolean {
  return typedParts(row) !== null;
}

export function toLocationCandidate(row: BbEntry<'locations'>): LocationMergeCandidate {
  return toCandidate(row, toMergeRow(row));
}

type LocationRowMetro = { metro?: string | null };

/** Metro coverage row — city chip, not a physical site. */
export function isMetroCoverageRow(row: BbEntry<'locations'>): boolean {
  const extra = row as BbEntry<'locations'> & LocationRowMetro;
  const metro = typeof extra.metro === 'string' && extra.metro.trim();
  const noAddress = !(typeof row.address === 'string' && row.address.trim());
  return Boolean(metro && noAddress && typedParts(row) === null);
}

/** Physical sites — not metro-only service-area chips. */
export function isAddressLocation(row: BbEntry<'locations'>): boolean {
  if (row.deleted_at !== null) return false;
  if (isPrimaryKey(row.location_key)) return true;
  if (isMetroCoverageRow(row)) return false;
  if (row.location_key?.startsWith('loc-') || row.location_key?.startsWith('clinic-')) {
    return true;
  }
  if (typeof row.address === 'string' && row.address.trim()) return true;
  return typedParts(row) !== null;
}

function toCandidate(row: BbEntry<'locations'>, mergeRow: MergeRow): LocationMergeCandidate {
  const extra = row as BbEntry<'locations'> & LocationRowExtras;
  return {
    id: row.id,
    locationKey: displayLocationKey(mergeRow.locationKey),
    verified: mergeRow.verified,
    parts: {
      street: mergeRow.street,
      suite: mergeRow.suite,
      city: mergeRow.city,
      state: mergeRow.state,
      zip: mergeRow.zip,
    },
    displayAddress: row.address,
    gbpUrl: row.gbp_url,
    trackingPhone: row.tracking_phone,
    enrichment: extra.enrichment ?? null,
  };
}

function involvesPrimary(pair: { keep: MergeRow; twin: MergeRow }): boolean {
  return (
    isPrimaryKey(pair.keep.locationKey) ||
    isPrimaryKey(pair.twin.locationKey) ||
    pair.keep.verified
  );
}

export interface LocationMergeResult {
  keep: LocationMergeCandidate | null;
  twin: LocationMergeCandidate | null;
  locationCount: number;
}

export function useLocationMerge(): LocationMergeResult {
  const locations = useBrandBibleWizardStore(selectEntries('locations'));

  return useMemo(() => {
    const live = locations.filter((row) => row.deleted_at === null);
    const byId = new Map(live.map((row) => [row.id, row]));
    const mergeRows = live.map(toMergeRow);
    const pairs = findDuplicatePairs(mergeRows);
    const flagged = pairs.find(involvesPrimary) ?? null;
    if (!flagged || !partsEqual(flagged.keep, flagged.twin)) {
      return { keep: null, twin: null, locationCount: live.length };
    }
    const keep = pickKeepRow(flagged.keep, flagged.twin);
    const twin = keep.id === flagged.keep.id ? flagged.twin : flagged.keep;
    const keepRow = byId.get(keep.id);
    const twinRow = byId.get(twin.id);
    if (!keepRow || !twinRow) {
      return { keep: null, twin: null, locationCount: live.length };
    }
    return {
      keep: toCandidate(keepRow, keep),
      twin: toCandidate(twinRow, twin),
      locationCount: live.length,
    };
  }, [locations]);
}
